import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import {
	AuthnService,
	CompanyService,
	ComponentBase,
	DeploymentContext,
	LogoSize,
	SearchService,
	WebAnalyticsService,
} from '@Common';
import {
	Company,
	Filter,
	LocalizedTextIds,
	Mode,
	orderByTagCount,
	TagWithCount,
	urlIdentifierForCompany,
} from 'company-finder-common';

@Component({
	selector: 'new-companies',
	templateUrl: './new-companies.component.html',
	styleUrls: ['./new-companies.component.scss'],
	standalone: false,
})
export class NewCompaniesComponent
	extends ComponentBase
	implements OnDestroy, OnInit
{
	public arrow = '&#x009b;';
	public logoSize: LogoSize = LogoSize.Small;
	public newCompanies: Company[];
	@Input()
	public tagCounts: TagWithCount[];

	private _fellbackToShowingXNewCompanies = false;

	public constructor(
		dc: DeploymentContext,
		private _authnService: AuthnService,
		private _companyService: CompanyService,
		private _router: Router,
		private _searchService: SearchService,
		private _webAnalyticsService: WebAnalyticsService
	) {
		super(dc);
	}

	public get title(): string {
		return this.Loc(
			this.isShowXNewCompaniesMode
				? LocalizedTextIds.RecentCompanies
				: LocalizedTextIds.MyUpdatesNewCompanies
		);
	}

	public get filterSubject(): Subject<Mode> {
		return this._searchService.filterSubject;
	}

	public get isShowXNewCompaniesMode(): boolean {
		return this._fellbackToShowingXNewCompanies;
	}

	public async handleArrowClick(company: Company): Promise<void> {
		if (
			this._authnService.isAuthenticated ||
			!this._deploymentContext.featureSwitches.enablePaywall
		) {
			this.navigateToCompany(company);
		} else {
			this.navigateToSearch();
		}
	}

	public handleViewMoreClick(): void {
		this.navigateToSearch();
	}

	private navigateToSearch(): void {
		const filter = this._searchService.filter;
		filter.isNewInLast90Days = true;

		// FUTURE: This was a quick hack related to ADJQ-210.  There could definitely be a cleaner way of
		// indicating that we should *always* transition to the search results in this case.
		this._searchService.currentSearchPredicate = ' ';
		this._searchService.drilldownSubject.next(Mode.Company);
		this._webAnalyticsService.trackEvent('new-companies-more');
	}

	private navigateToCompany(company: Company): void {
		this._router.navigate([
			'company',
			urlIdentifierForCompany(company.name),
		]);
		this._webAnalyticsService.trackEvent('new-companies-drilldown', {
			category: 'Company Details',
			label: company.name,
		});
	}

	public getEncodedName(company: Company): string {
		return urlIdentifierForCompany(company.name);
	}

	public async ngOnInit(): Promise<void> {
		await this.initializeNewCompanies();

		this.addSubscription(
			this.filterSubject.subscribe(async () => {
				await this.initializeNewCompanies();
			})
		);
	}

	public ngOnDestroy(): void {
		if (this._subscriptions) {
			this._subscriptions.unsubscribe();
			this._subscriptions = undefined;
		}
	}

	public tagsToDisplay(company: Company): string[] {
		return orderByTagCount(company, this.tagCounts);
	}

	private async initializeNewCompanies(): Promise<void> {
		// Deep copy the filter, if one exists, so that we preserve existing constraints,
		// and then further narrow the filter to just the "isNewInLast90Days" to reduce amount of data fetched
		const filterCopy = this._searchService.filter
			? this._searchService.filter.clone()
			: Filter.emptyFilter;
		filterCopy.isNewInLast90Days = true;

		let { items: companies } = await this._companyService.searchAndFilter(
			'',
			0,
			0,
			filterCopy
		);

		// ADJQ-2046: Because new companies are joining JLABS less frequently in 2026,
		// Matt wanted a fallback to the most recent 10, rather than an empty tile
		const fallbackCount =
			this._deploymentContext.featureSwitches.newCompaniesFallsBackToX ??
			0;
		if (companies.length == 0 && fallbackCount > 0) {
			this._fellbackToShowingXNewCompanies = true;
			filterCopy.isNewInLast90Days = false;
			filterCopy.showXNewCompanies = fallbackCount;
			// Server sorts by commencementDate desc and limits to showXNewCompanies results,
			// respecting per-user visibility rules so we always get the top X for the actual result set
			const results = await this._companyService.searchAndFilter(
				'',
				0,
				0,
				filterCopy
			);
			companies = results.items;
		}

		this.newCompanies = companies.sort(
			(a, b) =>
				new Date(b.commencementDate).getTime() -
				new Date(a.commencementDate).getTime()
		);
	}
}
